import { RiArrowLeftSLine } from 'react-icons/ri';
import { Link } from 'react-router-dom';

import feedback from 'assets/img/experienceCatalog/feedback.png';
import { useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { ExperienceCatalogQuestions } from 'types/experienceCatalog';
import {
  fetchFeedbackExperienceCatalog,
  fetchQuestionExperienceCatalog,
  getDeadLineExperienceCatalog,
  getRegulationExperienceCatalog,
  loadExperiencePurchase,
  registerFeedbackExperienceCatalog,
} from 'services/experienceCataolog.service';
import { useAuth } from 'context/AuthContext';
import { StarRating } from 'components/starRating';
import ArticlePlaceholder from 'components/ui/article-placeholder';

export default function ExperienceCatalogFeedbackPage() {
  Modal.setAppElement('#root');
  const { userInfo } = useAuth();
  const [question, setQuestion] = useState<ExperienceCatalogQuestions[]>();
  const [ratings, setRatings] = useState<Record<number, number>>({});
  const [comentario, setComentario] = useState('');
  const [identificar, setIdentificar] = useState(false);
  const [experienceInfo, setExperienceInfo] = useState<{
    title: string;
    description: string;
    date: Date | null;
    type: string;
    regrasDeUso?: any;
    valor?: string;
    total?: string;
  }>({
    title: '',
    description: '',
    date: null,
    type: '',
    regrasDeUso: undefined,
    valor: undefined,
    total: undefined,
  });
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [rulePage, setRulePage] = useState(0);
  const [ruleUrl, setRuleUrl] = useState('');
  const [hasFeedback, setHasFeedback] = useState(false);
  const [loading, setLoading] = useState(false);
  const [endEvent, setEndEvent] = useState(false);

  const handleStarClick = (questionIndex: number, rating: number) => {
    setRatings(prev => ({
      ...prev,
      [questionIndex]: rating,
    }));
  };

  const verifyExperience = useCallback(async () => {
    try {
      if (!userInfo || !userInfo.chave) {
        return;
      }
      const response = await loadExperiencePurchase(userInfo.chave, '1S2025');
      const rule = await getRegulationExperienceCatalog();
      if (response) {
        setExperienceInfo({
          title: response.extra.title || '',
          description: response.extra.description || '',
          date: response.time || null,
          type: response.extra.type || '',
          regrasDeUso: response.extra.regrasDeUso || undefined,
          valor: response.extra.valor || undefined,
          total: response.extra.total || undefined,
        });
      }
      if (rule) {
        setRuleUrl(rule.file?.url || '');
      }
    } catch (error) {
      console.error('Error verifying experience:', error);
    }
  }, [userInfo]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userInfo || !userInfo.chave) {
        return;
      }
      setLoading(true);
      const result = await fetchQuestionExperienceCatalog();
      const feedback = await fetchFeedbackExperienceCatalog(userInfo.chave);
      const deadLineResponse = await getDeadLineExperienceCatalog();

      let endDate;
      if (deadLineResponse.endDate?.$date) {
        endDate = new Date(deadLineResponse.endDate.$date);
      } else if (typeof deadLineResponse.endDate === 'string') {
        endDate = new Date(deadLineResponse.endDate);
      } else if (deadLineResponse.endDate instanceof Date) {
        endDate = deadLineResponse.endDate;
      } else {
        console.error('Invalid endDate format:', deadLineResponse.endDate);
        endDate = new Date();
      }
      if (deadLineResponse && endDate < new Date()) {
        setEndEvent(true);
      }

      setQuestion(result);
      setHasFeedback(feedback && feedback.length > 0);
      setLoading(false);
    };
    fetchData();
    verifyExperience();
  }, [userInfo, verifyExperience]);

  const sendFeedback = async () => {
    try {
      if (!userInfo || !userInfo.chave) {
        return;
      }

      const feedbackData = Object.entries(ratings).map(([questionIndex, rating]) => {
        const questionId = question?.[parseInt(questionIndex)]?._id;
        return {
          questionId,
          rating,
          pergunta: question?.[parseInt(questionIndex)]?.pergunta,
        };
      });
      const response = await registerFeedbackExperienceCatalog(userInfo.chave, comentario, feedbackData, identificar);
      console.log('Feedback enviado com sucesso:', response);
      setHasFeedback(true);
    } catch (error) {
      console.error('Error sending feedback:', error);
    }
  };

  if (loading) {
    return (
      <div
        className="text-center text-white flex flex-col items-center justify-center min-h-screen"
        role="status"
        aria-live="polite"
        aria-label="Carregando conteúdo da página"
      >
        <ArticlePlaceholder />
        <ArticlePlaceholder />
        <span className="sr-only">Carregando informações do catálogo de experiências...</span>
      </div>
    );
  }

  return (
    <div className="text-white min-h-screen">
      <header className="flex flex-col items-start py-10 container mx-auto lg:p-10">
        <div className="flex flex-row mb-4 justify-center items-center">
          <Link
            to="../"
            className="rounded-full bg-purple flex justify-center items-center p-3 mr-4 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:outline-none transition-all hover:bg-purple-700"
            aria-label="Voltar para página anterior"
          >
            <RiArrowLeftSLine size={28} className="text-white" />
          </Link>
          <h1
            className="text-lg lg:text-3xl py-1 px-6 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all"
            tabIndex={0}
            role="banner"
            aria-label="Página de feedback: Catálogos de Experiências"
          >
            Catálogos de Experiências
          </h1>
        </div>
      </header>

      <section aria-labelledby="experience-status">
        <div className="bg-maastricht-blue flex flex-col md:flex-row items-center py-12 px-4">
          {!!experienceInfo ? (
            <div className="flex flex-row items-center container mx-auto">
              <div className="px-10">
                <h2
                  id="experience-status"
                  className="text-4xl font-semibold text-electric-blue mb-4 max-w-2xl relative focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-maastricht-blue transition-all"
                  tabIndex={0}
                >
                  Parabéns! Você escolheu viver uma experiência.
                </h2>
                <div className="mt-8 gap-4 font-light">
                  <p
                    className="max-w-2xl text-xl text-center md:text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-maastricht-blue transition-all"
                    tabIndex={0}
                    role="status"
                    aria-label="Informações da experiência confirmada"
                  >
                    Experiência confirmada:{' '}
                    <span className="text-yellow">
                      {experienceInfo?.valor && experienceInfo.type === 'pontos_Livelo' && experienceInfo?.valor}{' '}
                      {experienceInfo?.total && experienceInfo.type === 'dias_livres' && experienceInfo?.total}{' '}
                      {experienceInfo.title}
                    </span>
                    <br />
                    {experienceInfo?.valor && experienceInfo.type === 'cartao' && (
                      <>
                        valor:{' '}
                        <span className="text-yellow">
                          {experienceInfo.type === 'cartao' ? 'R$' : ''} {experienceInfo?.valor}
                        </span>
                      </>
                    )}
                  </p>
                  {experienceInfo.type === 'combo' && (
                    <p
                      className="max-w-2xl text-xl text-center md:text-left mb-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-maastricht-blue transition-all"
                      tabIndex={0}
                    >
                      <span className="text-yellow">{experienceInfo.description}</span>
                    </p>
                  )}
                  <p
                    className="max-w-2xl text-xl text-center md:text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-maastricht-blue transition-all"
                    tabIndex={0}
                    role="status"
                    aria-label="Data de confirmação da experiência"
                  >
                    Data da confirmação:{' '}
                    <span className="text-yellow">
                      {experienceInfo.date ? new Date(experienceInfo.date).toLocaleDateString('pt-BR') : 'Carregando...'}
                    </span>
                  </p>
                  <div className="flex text-sm mt-2 items-center gap-1">
                    <span>Ver</span>
                    <button
                      className="font-bold underline hover:no-underline focus-visible:ring-2 focus-visible:ring-yellow focus-visible:ring-offset-1 focus-visible:outline-none rounded-sm py-1 transition-all"
                      onClick={() => setShowRulesModal(true)}
                      aria-label="Abrir modal com regras de uso da experiência"
                    >
                      regras de uso
                    </button>
                    <span>e</span>
                    <a
                      className="font-bold underline hover:no-underline focus-visible:ring-2 focus-visible:ring-yellow focus-visible:ring-offset-1 focus-visible:outline-none rounded-sm py-1 transition-all"
                      href={ruleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Abrir regulamento em nova aba (abre em nova janela)"
                    >
                      regulamento
                    </a>
                    <span>.</span>
                  </div>
                </div>
              </div>
              <img
                src={feedback}
                alt="Feedback"
                className="w-[300px] md:w-[520px] lg:w-[620px] right-[0%] lg:right-[20%] absolute"
              />
            </div>
          ) : (
            <div className="flex flex-row items-center container mx-auto px-10">
              <div>
                <h2
                  className="text-4xl font-semibold text-electric-blue mb-4 max-w-2xl relative focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-maastricht-blue transition-all"
                  tabIndex={0}
                  role="status"
                >
                  Você continua ganhando!
                </h2>
                <div>
                  <p
                    className="max-w-2xl text-xl text-center md:text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-maastricht-blue transition-all"
                    tabIndex={0}
                    role="status"
                    aria-live="polite"
                  >
                    O prazo para escolher sua experiência já foi encerrado, mas não se preocupe, seus pontos Livelo serão
                    creditados automaticamente na sua conta no site da Livelo.{' '}
                  </p>
                </div>
              </div>
              <img
                src={feedback}
                alt="Feedback"
                className="w-[300px] md:w-[520px] lg:w-[620px] right-[0%] lg:right-[20%] absolute"
              />
            </div>
          )}
        </div>
      </section>

      <Modal
        isOpen={showRulesModal}
        onRequestClose={() => {
          setShowRulesModal(false);
          setRulePage(0);
        }}
        contentLabel="Modal de Regras de Uso da Experiência"
        className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 relative outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        ariaHideApp={false}
        shouldFocusAfterRender={true}
        shouldReturnFocusAfterClose={true}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-content"
      >
        <button
          onClick={() => {
            setShowRulesModal(false);
            setRulePage(0);
          }}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 text-xl focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none rounded-full p-1 transition-all"
          aria-label="Fechar modal de regras de uso"
        >
          ✕
        </button>
        <div className="text-center">
          <h3
            id="modal-title"
            className="text-xl mb-4 text-[#002647] font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 transition-all"
            tabIndex={0}
          >
            Regras de uso
          </h3>
          <h4
            className="text-[#002647] font-bold mb-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 transition-all"
            tabIndex={0}
          >
            {experienceInfo.title}
          </h4>
          {experienceInfo.regrasDeUso?.rules2
            ? (() => {
                const rules = [experienceInfo.regrasDeUso.rules1, experienceInfo.regrasDeUso.rules2];
                return (
                  <div id="modal-content">
                    <div
                      className="text-sm text-[#002647] list-disc pl-5 space-y-1 custom-list-disc-yellow focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 transition-all"
                      dangerouslySetInnerHTML={{ __html: rules[rulePage] }}
                      tabIndex={0}
                      role="region"
                      aria-live="polite"
                      aria-atomic="true"
                      aria-label={`Conteúdo da página ${rulePage + 1} de 2 das regras de uso`}
                    />
                    <nav className="flex flex-col items-center justify-center mb-2" aria-label="Navegação das páginas de regras">
                      <div className="flex items-center gap-2 mb-1" role="tablist" aria-label="Páginas das regras de uso">
                        {[0, 1].map(idx => (
                          <button
                            key={idx}
                            type="button"
                            role="tab"
                            aria-label={`Página ${idx + 1} das regras de uso`}
                            aria-current={rulePage === idx ? 'page' : undefined}
                            aria-selected={rulePage === idx}
                            onClick={() => setRulePage(idx)}
                            className={`carousel-dot focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 focus-visible:outline-none rounded-full transition-all${rulePage === idx ? ' active' : ''}`}
                          />
                        ))}
                      </div>
                      <span
                        className="text-[#102535] text-sm font-normal leading-none"
                        role="status"
                        aria-live="polite"
                        aria-atomic="true"
                      >
                        {rulePage + 1} de 2
                      </span>
                    </nav>
                  </div>
                );
              })()
            : experienceInfo.regrasDeUso?.rules1 && (
                <div
                  id="modal-content"
                  className="text-sm text-[#002647] list-disc pl-5 space-y-1 custom-list-disc-yellow focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 transition-all"
                  dangerouslySetInnerHTML={{ __html: experienceInfo.regrasDeUso.rules1 }}
                  tabIndex={0}
                  role="region"
                  aria-label="Regras de uso da experiência"
                />
              )}
        </div>
        <div className="flex justify-center mt-6">
          <button
            onClick={() => {
              setShowRulesModal(false);
              setRulePage(0);
            }}
            className="bg-yellow text-oxford-blue px-6 py-3 rounded-lg font-bold text-lg hover:bg-yellow-400 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none transition-all"
          >
            Fechar
          </button>
        </div>
      </Modal>
      {!endEvent && (
        <>
          {!hasFeedback ? (
            <main className="bg-slate-blue py-12 px-4 container mx-auto" role="main" aria-labelledby="feedback-main-heading">
              <section className="mb-8">
                <div className="flex flex-col items-start justify-start container mx-auto">
                  <h2
                    id="feedback-main-heading"
                    className="text-3xl font-bold ml-20 w-2/4 mb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-blue transition-all"
                    tabIndex={0}
                  >
                    Gostaríamos de saber como foi sua opinião! Faça a avaliação abaixo:
                  </h2>
                </div>
              </section>

              <section aria-labelledby="feedback-questions">
                <h3 id="feedback-questions" className="sr-only">
                  Perguntas de avaliação
                </h3>
                <div className="flex flex-col items-start justify-start container mx-auto px-20">
                  {question &&
                    question.map((item, index) => (
                      <fieldset
                        key={index}
                        className="mb-6 w-full bg-white rounded-lg border-slate-400 border-2 flex items-center justify-between p-6 focus-within:ring-2 focus-within:ring-yellow focus-within:ring-offset-2 transition-all shadow-md"
                      >
                        <legend className="text-xl border-slate-400 border-2 font-bold text-slate-900 px-3 py-1 bg-white">
                          Pergunta {index + 1}
                        </legend>
                        <div className="text-lg font-semibold text-slate-800 px-2 max-w-md">{item.pergunta}</div>
                        <div className="flex flex-col items-center ml-4">
                          <StarRating
                            rating={ratings[index] || 0}
                            onRating={rate => handleStarClick(index, rate)}
                            aria-label={`Avaliação de 1 a 5 estrelas para: ${item.pergunta}`}
                          />
                          <div aria-live="polite" aria-atomic="true" className="sr-only">
                            {ratings[index]
                              ? `Avaliação atual: ${ratings[index]} de 5 estrelas para "${item.pergunta}"`
                              : `Nenhuma avaliação selecionada para "${item.pergunta}"`}
                          </div>
                        </div>
                      </fieldset>
                    ))}
                </div>
              </section>
              <section className="w-full px-20 mt-4" aria-labelledby="additional-feedback">
                <div className="mb-6">
                  <label
                    htmlFor="comentario"
                    className="block text-white text-xl font-semibold mb-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-blue transition-all"
                    tabIndex={0}
                  >
                    Comentários e sugestões (opcional):
                  </label>
                  <textarea
                    id="comentario"
                    className="w-full h-32 rounded-md p-4 text-black focus-visible:ring-2 focus-visible:ring-yellow focus-visible:ring-offset-2 focus-visible:outline-none border-2 border-gray-300 focus:border-yellow-400 transition-all resize-vertical"
                    placeholder="O que você mais gostou nesse modelo; o que pode melhorar; etc..."
                    value={comentario}
                    onChange={e => setComentario(e.target.value)}
                    aria-describedby="comentario-hint"
                  />
                  <div id="comentario-hint" className="text-white text-sm mt-1">
                    Compartilhe suas impressões sobre a experiência escolhida
                  </div>
                </div>

                <fieldset className="mt-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-blue transition-all rounded-md p-2">
                  <legend
                    className="text-white text-lg mb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-blue transition-all px-2"
                    tabIndex={0}
                  >
                    Deseja se identificar:
                  </legend>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 text-white cursor-pointer hover:bg-white/10 focus-within:bg-white/10 rounded-md p-2 transition-all">
                      <input
                        type="radio"
                        name="identificar"
                        value="nao"
                        checked={!identificar}
                        onChange={() => setIdentificar(false)}
                        className="w-4 h-4 text-yellow focus:ring-2 focus:ring-yellow focus:ring-offset-1"
                        aria-describedby="nao-identificar-desc"
                      />
                      <span>Não</span>
                      <span id="nao-identificar-desc" className="sr-only">
                        Não desejo me identificar no feedback
                      </span>
                    </label>
                    <label className="flex items-center gap-2 text-white cursor-pointer hover:bg-white/10 focus-within:bg-white/10 rounded-md p-2 transition-all">
                      <input
                        type="radio"
                        name="identificar"
                        value="sim"
                        checked={identificar}
                        onChange={() => setIdentificar(true)}
                        className="w-4 h-4 text-yellow focus:ring-2 focus:ring-yellow focus:ring-offset-1"
                        aria-describedby="sim-identificar-desc"
                      />
                      <span>Sim</span>
                      <span id="sim-identificar-desc" className="sr-only">
                        Desejo me identificar no feedback
                      </span>
                    </label>
                  </div>
                </fieldset>

                <p
                  className="text-white mt-4 max-w-2xl text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-blue transition-all rounded-md p-1"
                  tabIndex={0}
                  role="note"
                >
                  Essa avaliação de escolha de experiências faz parte de um teste e portanto o resultado final será utilizado
                  somente para avaliação dessa nova metodologia.
                </p>
              </section>
              {/* Botão de envio */}
              <div className="flex justify-center mt-8">
                <button
                  onClick={() => {
                    sendFeedback();
                  }}
                  className="bg-yellow text-oxford-blue px-8 py-3 rounded-lg font-bold text-xl hover:bg-yellow-400 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!question || Object.keys(ratings).length < question.length}
                  aria-describedby={question && Object.keys(ratings).length < question.length ? 'submit-hint' : undefined}
                >
                  {!question
                    ? 'Carregando...'
                    : Object.keys(ratings).length < question.length
                      ? `Enviar Avaliação (${Object.keys(ratings).length}/${question.length})`
                      : 'Enviar Avaliação'}
                </button>
                {question && Object.keys(ratings).length < question.length && (
                  <div id="submit-hint" className="sr-only">
                    Por favor, responda todas as {question.length} perguntas para enviar sua avaliação
                  </div>
                )}
              </div>
            </main>
          ) : (
            <main className="bg-slate-blue py-12 px-4 container mx-auto" role="main">
              <section role="status" aria-live="polite" aria-labelledby="feedback-success">
                <div className="flex flex-col items-start justify-start container mx-auto">
                  <h2
                    id="feedback-success"
                    className="text-3xl font-bold ml-20 w-2/4 mb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-blue transition-all"
                    tabIndex={0}
                  >
                    Agradecemos por compartilhar sua opinião!
                  </h2>
                  <p
                    className="text-xl font-normal ml-20 w-2/4 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-blue transition-all"
                    tabIndex={0}
                  >
                    Seu feedback é muito importante para continuarmos melhorando nossas experiências.
                  </p>
                </div>
              </section>
            </main>
          )}
        </>
      )}
    </div>
  );
}
